import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DNS_PROVIDERS, OTHER_PROVIDER, matchProviderKey } from './domainState';
import { DnsHelpPanel } from './DnsHelpPanel';
import type { AskFailure, DomainHelp } from './useDomainHelp';
import { FIELD_WIDTH } from '@/theme';

export interface ProviderStepProps {
  help: DomainHelp | null;
  questionsLeft: number | null;
  exhausted: boolean;
  askFailure?: AskFailure | null;
  aiUnavailable?: boolean;
  starting?: boolean;
  asking?: boolean;
  disabled?: boolean;
  onStart: (provider?: string) => Promise<void>;
  onAsk: (question: string) => Promise<boolean>;
}

export function ProviderStep({
  help,
  questionsLeft,
  exhausted,
  askFailure = null,
  aiUnavailable = false,
  starting,
  asking,
  disabled,
  onStart,
  onAsk,
}: ProviderStepProps) {
  const { t } = useTranslation();
  const [pick, setPick] = useState<string>(OTHER_PROVIDER);
  const [custom, setCustom] = useState('');

  useEffect(() => {
    if (!help?.detectedProvider) return;
    const matched = matchProviderKey(help.detectedProvider);
    setPick(matched);
    if (matched === OTHER_PROVIDER) setCustom(help.detectedProvider);
  }, [help?.detectedProvider]);

  const providerValue = (): string | undefined => {
    if (pick === OTHER_PROVIDER) {
      const trimmed = custom.trim().slice(0, 60);
      return trimmed.length > 0 ? trimmed : undefined;
    }
    return pick;
  };

  const providerName =
    pick === OTHER_PROVIDER
      ? custom.trim() || t('domain.providers.OTHER')
      : t(`domain.providers.${pick}`);

  const handleStart = () => {
    void onStart(providerValue());
  };

  // Enter in the provider field submits (a real form); the rewrite button below calls it directly.
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (starting) return;
    handleStart();
  };

  const nameservers = help?.nameservers ?? [];

  return (
    <Stack spacing={2}>
      {nameservers.length > 0 ? (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="overline">{t('domain.nsFound')}</Typography>
          <Typography variant="mono" component="p">
            {t('domain.nsLine', { list: nameservers.join(', ') })}
          </Typography>
        </Box>
      ) : null}
      {help?.detectedProvider ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('domain.detected', { provider: help.detectedProvider })}
        </Typography>
      ) : null}

      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <FormControl sx={{ minWidth: FIELD_WIDTH.sm, flex: 1, maxWidth: FIELD_WIDTH.md }} disabled={disabled}>
          <InputLabel id="domain-provider-label">{t('domain.provider')}</InputLabel>
          <Select
            labelId="domain-provider-label"
            label={t('domain.provider')}
            value={pick}
            onChange={(e: SelectChangeEvent<string>) => setPick(e.target.value)}
          >
            {DNS_PROVIDERS.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`domain.providers.${key}`)}
              </MenuItem>
            ))}
            <MenuItem value={OTHER_PROVIDER}>{t('domain.providers.OTHER')}</MenuItem>
          </Select>
        </FormControl>
        {pick === OTHER_PROVIDER ? (
          <TextField
            label={t('domain.providerOther')}
            value={custom}
            onChange={(e) => setCustom(e.target.value.slice(0, 60))}
            placeholder={t('domain.providerOtherPlaceholder')}
            disabled={disabled}
            sx={{ minWidth: FIELD_WIDTH.sm, flex: 1, maxWidth: FIELD_WIDTH.md }}
          />
        ) : null}
        {disabled ? null : (
          <Button type="submit" variant="outlined" loading={starting === true}>
            {t('domain.getInstructions')}
          </Button>
        )}
      </Box>

      {starting ? (
        <Alert icon={<CircularProgress size={16} />} severity="info">
          {t('domain.help.writing', { provider: providerName })}
        </Alert>
      ) : null}

      {help ? (
        <DnsHelpPanel
          help={help}
          providerName={providerName}
          questionsLeft={questionsLeft}
          exhausted={exhausted}
          askFailure={askFailure}
          aiUnavailable={aiUnavailable}
          asking={asking === true}
          disabled={disabled === true}
          onRewrite={handleStart}
          onAsk={onAsk}
        />
      ) : null}
    </Stack>
  );
}
